import {useMemo} from 'react';
import {useSelector} from 'react-redux';
import {reduxSelectCurrentUser} from 'app/hooks/reduxSelectCurrentUser';
import {MatchFlag} from 'app/types/match/MatchFlags';

interface SofttickerMatch {
  flags: string[] | null;
  softtickerResultReportableAt: string | null;
  softtickerEventsReportableUntil: string | null;
  competition: {
    district: {
      slug: string;
    };
  };
}

const checkIsReportable = (reportableAt: string | null): boolean => {
  if (!reportableAt) {
    return false;
  }
  const reportableAtDate = new Date(reportableAt).getTime();
  const now = new Date().getTime();
  return reportableAtDate <= now;
};

const checkIsReportableUntil = (reportableUntil: string | null): boolean => {
  if (!reportableUntil) {
    return false;
  }
  const reportableUntilDate = new Date(reportableUntil).getTime();
  const now = new Date().getTime();
  return reportableUntilDate >= now;
};

// TODO: Remove workaround and replace with region settings as soon as option is available in match response
//  https://fupa.atlassian.net/browse/DV-17371
const disabledDistricts = ['luxemburg'];
const isAllowedMatchDistrict = (districtSlug: string): boolean => {
  return !disabledDistricts.includes(districtSlug);
};

export const useIsSofttickerEligible = (match: SofttickerMatch | undefined): boolean => {
  return useMemo(() => {
    if (!match) {
      return false;
    }
    const isAllowedDistrict = isAllowedMatchDistrict(match.competition?.district?.slug);
    const hasTicker = !!match.flags?.includes(MatchFlag.Ticker);
    const isReportable = checkIsReportable(match.softtickerResultReportableAt);
    return isAllowedDistrict && isReportable && !hasTicker;
  }, [match]);
};

const useAllowedUser = (): boolean => {
  const {fuPaUser} = useSelector(reduxSelectCurrentUser);

  return useMemo(() => {
    return !!fuPaUser?.id;
  }, [fuPaUser]);
};

export default function useAllowSoftTicker(match: SofttickerMatch | undefined): boolean {
  const isAllowedUser = useAllowedUser();
  const isSoftTickerEligible = useIsSofttickerEligible(match);

  return isSoftTickerEligible && isAllowedUser;
}

function useIsSofttickerEventEligible(match: SofttickerMatch) {
  return useMemo(() => {
    const hasSoftticker = !!match.flags?.includes('softticker');
    const isReportable = checkIsReportableUntil(match.softtickerEventsReportableUntil);
    return isReportable && hasSoftticker;
  }, [match]);
}

export function useAllowSoftTickerEvents(match: SofttickerMatch): boolean {
  const isAllowedUser = useAllowedUser();
  const isSoftTickerEventEligible = useIsSofttickerEventEligible(match);

  return isAllowedUser && isSoftTickerEventEligible;
}
